import { Box, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
// import AsyncSelect from "../../../../../../../components/AsyncSelect";
import CustomSelect from "../../../../../../../components/CustomSelect";
import CustomTextArea from "../../../../../../../components/CustomTextArea";
import Input from "../../../../../../../components/Input";
import StyledFormModal from "../../../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import useSelectedPatient from "../../../../../../../hooks/local-storage/use-selected-patient";
// import { useDebounce } from "../../../../../../../hooks/use-debounce";
import { DisclosureHost } from "../../../../../../../hooks/use-disclosure";
import { useQueryParams } from "../../../../../../../hooks/use-query-param";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
// import useGetCataLogCode from "../../../../../../../queries/catalog/use-get-catalog-code";
import { usePastFamilyHistoryCreate } from "../../../../../../../queries/history/past-family/use-patient-family-history-create";
import { usePastFamilyHistoryUpdate } from "../../../../../../../queries/history/past-family/use-patient-family-history-update";
import { usePastFamilyHistoryList } from "../../../../../../../queries/history/past-family/use-patient-family-history-list";
import { ExportClinicalDataToEncounterTypeEnum } from "../../../../../../../services";
import { useExportClinicalToEncounter } from "../../../../../../../queries/encounter/use-export-clinical-to-encounter";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getErrorSnackbar,
  getSuccessSnackbar,
  // getValueFromObj,
} from "../../../../../../../utils/utils";
import LabeledSwitch from "../../../../../../../components/LabeledSwitch";
import MaskMultiSelectScroll from "../../../../../../../components/MaskMultiSelectScroll";
import {
  DIED_LIST,
  FAMILY_INITIAL_VALUES,
  FAMILY_VALIDATION_SCHEMA,
  FamilyHistoryForm,
  RELATIVE_LIST,
} from "./history-constant";
import NumberInput from "../../../../../../../components/NumberInput";
import { formatDateTime } from "../../../../../../../utils/date-time-utility";
import { getStoredRealm } from "@/utils/auth-storage";

interface UpsertFamilyHistoryProps {
  disclosureHost: DisclosureHost;
}

const UpsertFamilyHistory = ({ disclosureHost }: UpsertFamilyHistoryProps) => {
  const prefillData = disclosureHost?.data as FamilyHistoryForm;
  const [selectedPatient] = useSelectedPatient();
  const { patientId } = useParams() as { patientId: any };
  const appointmentid = useQueryParams("appointmentid");
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { mutateAsync: createPastFamilyHistory } = usePastFamilyHistoryCreate();
  const { mutateAsync: updatePastFamilyHistory } = usePastFamilyHistoryUpdate();
  const { mutateAsync: exportClinical } = useExportClinicalToEncounter();
  // const [searchKey, setSearchKey] = useDebounce("");

  const isVisitNotePage = window.location.pathname.includes("/visit-note");
  const [variant, setVariant] = useState<"import" | "default">("default");
  const [selectedRecords, setSelectedRecords] = useState<any>();

  const [familyPagination, setFamilyPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);

  const { data: familyHistoryList, isFetching: isFamilyHistoryFetching } =
    usePastFamilyHistoryList({
      patientUuid: patientId ?? "",
      pagination: familyPagination,
      gridSorting: [],
      archive: false,
      status: true,
      enabled: variant === "import",
    });

  const familyHistoryOptions = useMemo(() => {
    const fetched =
      familyHistoryList?.data.content.map((item: any) => ({
        value: item.uuid,
        label:
          `${item?.conditionName || ""} | ${formatDateTime(item?.recordedDate)}` ||
          "",
      })) ?? [];
    const selected = (selectedRecords ?? []) as { [k: string]: string }[];
    const idKey = Object.keys(fetched[0] ?? selected[0] ?? {})[0];
    const missing = selected.filter(
      (s) => !fetched.some((o) => (o as any)[idKey] === s[idKey])
    );
    return [...missing, ...fetched];
  }, [familyHistoryList?.data.content, selectedRecords]);

  const formik = useFormik<FamilyHistoryForm>({
    initialValues: prefillData ?? FAMILY_INITIAL_VALUES,
    validationSchema: FAMILY_VALIDATION_SCHEMA,
    onSubmit: handleSaveFamilyHistory,
    enableReinitialize: true,
  });

  // const { data: icdCodesRaw } = useGetCataLogCode({
  //   pagination: DEF_PAGINATION,
  //   searchKey,
  //   type: "ICD",
  //   active: true,
  //   archive: false,
  //   enabled: disclosureHost.opened,
  // });

  // const allIcdCodes = useMemo(() => {
  //   return (
  //     icdCodesRaw?.data?.content?.map((l) => ({
  //       uuid: l?.uuid,
  //       name: `${l?.code} - ${l?.description || ""}`,
  //     })) ?? []
  //   );
  // }, [icdCodesRaw]);

  async function handleSaveFamilyHistory(values: FamilyHistoryForm) {
    const problems = values?.problems
      ? { [values.problems.uuid]: values?.problems?.name }
      : undefined;

    const patient = {
      [patientId ? patientId : selectedPatient?.uuid]: patientId
        ? ""
        : selectedPatient?.firstName + " " + selectedPatient?.lastName,
    };

    const payload = {
      familyHistory: {
        ...values,
        patient,
        appointmentId: appointmentid ?? undefined,
        problems,
      },
      xTENANTID: getStoredRealm() ?? undefined,
    };

    if (prefillData) showToastAndResetForm(updatePastFamilyHistory(payload));
    else showToastAndResetForm(createPastFamilyHistory(payload));

    return values;

    async function showToastAndResetForm(apiCall: Promise<any>) {
      apiCall
        .then((res) => {
          closeAndResetForm();

          queryClient.invalidateQueries({
            queryKey: ["patient-family-history-list"],
          });

          showSnackbar(getSuccessSnackbar(res));
        })
        .catch((err) => showSnackbar(getErrorSnackbar(err)));
    }
  }

  async function handleImportFamilyHistory() {
    const recordUuids = (selectedRecords ?? [])
      .map((record: any) => record?.value)
      .filter(Boolean);

    if (!recordUuids.length) {
      showSnackbar(getErrorSnackbar("Please select at least one record."));
      return;
    }

    exportClinical({
      type: ExportClinicalDataToEncounterTypeEnum.FamilyHistory,
      appointmentUuid: appointmentid ?? "",
      recordUuids,
      xTENANTID: getStoredRealm() ?? undefined,
    })
      .then((res) => {
        closeAndResetForm();

        queryClient.invalidateQueries({
          queryKey: ["patient-family-history-list"],
        });

        showSnackbar(getSuccessSnackbar(res));
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  }

  function closeAndResetForm() {
    formik.resetForm();
    setVariant("default");
    setSelectedRecords(undefined);
    disclosureHost.close();
  }

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={closeAndResetForm}
      dialogTitle={prefillData ? "Edit Family History" : "Add Family History"}
      dialogAction={
        <StyledButtonV2
          label={variant === "import" ? "Import" : "Save"}
          variant="contained"
          onClick={
            variant === "import" ? handleImportFamilyHistory : formik.submitForm
          }
        />
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "450px",
        },
      }}
    >
      {isVisitNotePage && (
        <Box pt={1} px={1.5}>
          <LabeledSwitch
            label="import from existing records"
            checked={variant === "import"}
            onChange={(e) => {
              setVariant(e.target.checked ? "import" : "default");
            }}
          />
        </Box>
      )}

      {variant === "import" && (
        <Grid container spacing={3} pt={0}>
          <Grid item xs={12}>
            <MaskMultiSelectScroll
              required
              loading={isFamilyHistoryFetching}
              label="Condition Name"
              optionWithCheckbox={true}
              placeholder="Select from Existing Family History Records"
              options={familyHistoryOptions}
              value={selectedRecords}
              onChange={(v) => setSelectedRecords(v)}
              pagination={familyPagination}
              setPagination={setFamilyPagination}
              totalElements={familyHistoryList?.data?.page?.totalElements}
            />
          </Grid>
        </Grid>
      )}

      {variant === "default" && (
        <Grid container spacing={3} pt={isVisitNotePage ? 1 : 2}>
          <Grid item xs={12}>
            {/* <AsyncSelect
              required
              label="Condition Name"
              name="problems"
              handleBlur={formik.handleBlur}
              placeholder="Select or Search Problem"
              onSearch={setSearchKey}
              options={allIcdCodes}
              formatKeys={{ val: "uuid", label: "name" }}
              value={formik.values?.problems as any}
              onChange={(v) => formik.setFieldValue("problems", v)}
              error={formik.touched?.problems && formik.errors?.problems}
            /> */}

            {/* conditionName */}
            <Input
              required
              label="Condition Name"
              textFieldProps={{
                placeholder: "Type here",
                name: "conditionName",
                value: formik.values?.conditionName,
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
              }}
              error={
                (formik.touched?.conditionName as any) &&
                formik.errors?.conditionName
              }
            />
          </Grid>
          <Grid item xs={6}>
            <CustomSelect
              required
              name="relative"
              handleBlur={formik.handleBlur}
              label="Relative"
              placeholder="Select Relative"
              options={RELATIVE_LIST}
              value={formik.values?.relative}
              onChange={(v) => formik.setFieldValue("relative", v)}
              error={formik.touched?.relative && formik.errors?.relative}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInput
              label="Onset Age"
              textFieldProps={{
                placeholder: "Enter Age",
                name: "onSetAge",
                value: formik.values.onSetAge,
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <CustomSelect
              label="Died"
              placeholder="Select Status"
              options={DIED_LIST}
              value={formik.values?.died ? "yes" : "no"}
              onChange={(v) => formik.setFieldValue("died", v === "yes")}
            />
          </Grid>

          <Grid item xs={12}>
            <CustomTextArea
              label="Note"
              id="outlined-multiline-static"
              rows={3}
              variant="outlined"
              name="note"
              placeholder="Type here"
              onBlur={formik.handleBlur}
              value={formik.values?.note}
              onChange={formik.handleChange}
            />
          </Grid>
        </Grid>
      )}
    </StyledFormModal>
  );
};

export default UpsertFamilyHistory;
